import { BooleanFormInput } from './components/BooleanFormInput'
import { ChatFormInput } from './components/ChatFormInput'
import { CollectionsFormInput } from './components/CollectionsFormInput'
import { ConnectionFormInput } from './components/ConnectionFormInput'
import { DateFormInput } from './components/DateFormInput'
import { DynamicInputButton } from './components/DynamicInputButton'
import { FieldFormInput } from './components/FieldFormInput'
import { FieldOperationConfig, FieldOperationConfigTemplateInput, FieldOperationConfigTextInput, UUID } from '@indigohive/cogfy-types'
import { FileFormInput } from './components/FileFormInput'
import { FormInputProps, FormInputSchema } from './types'
import { JsonFormInput } from './components/JsonFormInput'
import { NumberFormInput } from './components/NumberFormInput'
import { ProviderName, providers } from '@indigohive/cogfy-providers'
import { TemplateFormInput } from './components/TemplateFormInput'
import { TextFormInput } from './components/TextFormInput'
import { VectorFormInput } from './components/VectorFormInput'
import { UseQueryResult } from '@tanstack/react-query'
import { GetConnectionsListResult } from '@indigohive/cogfy-types/endpoints/getConnectionsList'

export type AutogeneratedFormProps = {
  collectionId: UUID
  fieldId: UUID
  value: FieldOperationConfig
  onChange: (value: FieldOperationConfig) => void
  schema: Record<string, FormInputSchema>
  connections: FormInputProps['connections']
  eligibleCogfyCreditsProviders: FormInputProps['eligibleCogfyCreditsProviders']
  collections: FormInputProps['collections']
  fields: FormInputProps['fields']
  getConnections?: UseQueryResult<GetConnectionsListResult, Error>
}

type FormInput = { name: string } & FormInputSchema

const INPUTS_BY_TYPE: Record<string, (props: FormInputProps) => JSX.Element> = {
  boolean: BooleanFormInput,
  chat: ChatFormInput,
  collection: CollectionsFormInput,
  date: DateFormInput,
  file: FileFormInput,
  field: FieldFormInput,
  number: NumberFormInput,
  template: TemplateFormInput,
  json: JsonFormInput,
  text: TextFormInput,
  vector: VectorFormInput
}

const PROVIDER_NAMES = new Set(providers.map(provider => provider.name))

export function AutogeneratedForm (props: AutogeneratedFormProps) {
  const { schema, value, onChange } = props

  const inputs: FormInput[] = Object.entries(schema).map(([key, value]) => ({ name: key, ...value }))

  const onClickDynamicInputButton = (input: FormInput) => {
    const inputType = value[input.name]?.type

    if (inputType === 'text' || !inputType) {
      const currentValue = inputType ? (value[input.name] as FieldOperationConfigTextInput | undefined)?.text?.value : ''
      const newValue = Array.isArray(currentValue) ? [] : [currentValue ?? '']

      onChange({ ...value, [input.name]: { type: 'template', template: { strings: newValue, args: [] } } })
    } else {
      const templateInput = value[input.name] as FieldOperationConfigTemplateInput | undefined
      const currentArgs = templateInput?.template?.args ?? []
      const currentValue = templateInput?.template?.strings[0] ?? ''
      const hasCorrespondingOption = input.options?.map(option => option.value).includes(currentValue)

      if (!hasCorrespondingOption || currentArgs.length > 0) {
        onChange({ ...value, [input.name]: { type: 'text', text: { value: '' } } })
      } else {
        onChange({ ...value, [input.name]: { type: 'text', text: { value: currentValue } } })
      }
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {inputs.map(input => (
        <div key={input.name} className="fieldset">
          <legend className='w-full flex justify-between align-center'>
            <span className="text-sm">
              {input.displayName ?? input.name}
              {input.required && <span className="text-error"> *</span>}
              {input.type !== 'boolean' && (
                <span className="text-xs opacity-40 ml-2">
                  {input.type}
                </span>
              )}
            </span>

            {input.type === 'text' && input.options && !input.multiple && (
              <DynamicInputButton
                input={value[input.name]}
                onClick={() => onClickDynamicInputButton(input)}
              />
            )}
          </legend>

          {getComponentByType(input.type)?.({
            collectionId: props.collectionId,
            fieldId: props.fieldId,
            value: value[input.name],
            onChange: newValue => {
              if (newValue === undefined) {
                const nextValue = { ...value }
                delete nextValue[input.name]
                onChange(nextValue)
              } else {
                onChange({ ...value, [input.name]: newValue })
              }
            },
            fields: props.fields,
            connections: props.connections,
            eligibleCogfyCreditsProviders: props.eligibleCogfyCreditsProviders,
            collections: props.collections,
            schema: input,
            getConnections: PROVIDER_NAMES.has(input.type as ProviderName) ? props.getConnections : undefined
          })}

          {input.default !== undefined && !Array.isArray(input.default) && (
            <div className="label opacity-40">
              <span className="label-text-alt">
                Default: {input.default}
              </span>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

function getComponentByType (type: string): ((props: FormInputProps) => JSX.Element) | undefined {
  if (PROVIDER_NAMES.has(type as ProviderName)) {
    return ConnectionFormInput
  }

  return INPUTS_BY_TYPE[type]
}
